import { theme } from '@resistapp/client/components/shared/theme';
import { friendlyCopyNumber } from '@resistapp/common/friendly';
import { AxisBottom, AxisLeft } from '@visx/axis';
import { Group } from '@visx/group';
import { scaleLinear } from '@visx/scale';
import { LinePath } from '@visx/shape';
import { chartLayoutValues } from './bar-box-map/utils';

export interface Curve {
  xs: number[];
  ys: number[];
  color: string;
  id: string;
  /** Optional x-axis position (in data units) at which a vertical marker line should be drawn for this curve. */
  markerX?: number;
  /** Whether this curve corresponds to a disqualified tech rep */
  isRejected?: boolean;
  /** Whether this curve has no Ct (not detected) */
  isNotDetected?: boolean;
}

interface Props {
  curves: Curve[];
  width: number;
  height: number;
  showRejected?: boolean;
}

interface YAxisRange {
  yChartMin: number;
  yChartMax: number;
}

export function SimpleLineChart({ curves, width, height, showRejected = false }: Props) {
  const margin = { top: 10, right: 20, bottom: 40, left: 50 };

  const xValuesRaw = curves.flatMap(c => c.xs);
  const yValuesRaw = curves.flatMap(c => c.ys);

  const xValues = xValuesRaw.filter(x => Number.isFinite(x));
  const yValues = yValuesRaw.filter(y => Number.isFinite(y));

  const xChartMin: number = xValues.length === 0 ? 0 : xValues.reduce((min, v) => (v < min ? v : min), xValues[0]);
  const xChartMax: number = xValues.length === 0 ? 1 : xValues.reduce((max, v) => (v > max ? v : max), xValues[0]);

  const { yChartMin, yChartMax } = calculateYAxisRange(yValues);

  const innerWidth = width - margin.left - margin.right;
  const innerHeight = height - margin.top - margin.bottom;

  const xScale = scaleLinear({ domain: [xChartMin, xChartMax], range: [0, innerWidth] });
  const yScale = scaleLinear({ domain: [yChartMin < 0 ? yChartMin : 0, yChartMax], range: [innerHeight, 0] });

  // Calculate major tick values (approximate, since visx calculates them internally)
  const numMajorTicks = 6;
  const majorTickValues = xScale.ticks(numMajorTicks);

  // Calculate all integer values in the range
  const minInt = Math.ceil(xChartMin);
  const maxInt = Math.floor(xChartMax);
  const allIntegers = Array.from({ length: maxInt - minInt + 1 }, (_, i) => minInt + i);

  // Filter out integers that are already major ticks (within small tolerance)
  const minorTickValues = allIntegers.filter(int => {
    return !majorTickValues.some(majorTick => Math.abs(int - majorTick) < 0.1);
  });

  return (
    <svg width={width} height={height}>
      <Group left={margin.left} top={margin.top}>
        {/* Plot border */}
        <rect
          x={0}
          y={0}
          width={innerWidth}
          height={innerHeight}
          rx={8}
          ry={8}
          fill="none"
          stroke={theme.colors.neutral400}
        />

        {/* Data lines */}
        {curves.map(curve => (
          <LinePath
            key={curve.id}
            data={curve.xs.map((x, i) => ({ x, y: curve.ys[i] }))}
            x={d => xScale(d.x)}
            y={d => yScale(d.y)}
            stroke={curve.color}
            strokeWidth={1}
            strokeDasharray={showRejected && curve.isRejected ? '4,4' : curve.isNotDetected ? '1,4' : undefined}
            fill="none"
          />
        ))}

        {/* Vertical marker lines (Ct/Tm) - shown only when there are 3 or fewer curves */}
        {curves.length <= 3 &&
          curves.map(curve => {
            if (curve.markerX === undefined || !Number.isFinite(curve.markerX)) return null;
            const xPos = xScale(curve.markerX);
            return (
              <line
                key={`marker-${curve.id}`}
                x1={xPos}
                x2={xPos}
                y1={0}
                y2={innerHeight}
                stroke={curve.color}
                strokeWidth={1}
                strokeDasharray={showRejected && curve.isRejected ? '4,4' : curve.isNotDetected ? '1,4' : undefined}
              />
            );
          })}

        {/* Minor tick marks for integers */}
        {minorTickValues.map(tickValue => (
          <line
            key={`minor-tick-${tickValue}`}
            x1={xScale(tickValue)}
            x2={xScale(tickValue)}
            y1={innerHeight}
            y2={innerHeight + 2}
            stroke={theme.colors.neutral400}
            strokeWidth={1}
          />
        ))}

        {/* Axes */}
        <AxisLeft
          scale={yScale}
          left={-chartLayoutValues.yAxisMarginLeft}
          tickStroke={theme.colors.neutral600}
          stroke="transparent"
          tickLength={4}
          hideAxisLine
          numTicks={6}
          tickFormat={value => friendlyCopyNumber(Number(value))}
          tickLabelProps={tickValue => ({
            fontSize: 14,
            fill: theme.colors.neutral700 as string,
            textAnchor: 'end',
            x: -12,
            y: yScale(tickValue) + 4,
          })}
        />
        <AxisBottom
          scale={xScale}
          top={innerHeight}
          tickStroke={theme.colors.neutral400}
          stroke="transparent"
          tickLength={4}
          hideAxisLine
          numTicks={6}
          tickFormat={value => friendlyCopyNumber(Number(value))}
          tickLabelProps={{
            fontSize: 14,
            fill: theme.colors.neutral700 as string,
            textAnchor: 'middle',
            dy: 16,
          }}
        />
      </Group>
    </svg>
  );
}

function calculateYAxisRange(yValues: number[]): YAxisRange {
  if (yValues.length === 0) {
    return { yChartMin: 0, yChartMax: 1 };
  }

  const yMinOriginal = yValues.reduce((min, v) => (v < min ? v : min), yValues[0]);
  const yMaxOriginal = yValues.reduce((max, v) => (v > max ? v : max), yValues[0]);
  const dataRange = yMaxOriginal - yMinOriginal;

  const padding = dataRange === 0 ? (yMaxOriginal === 0 ? 1 : Math.abs(yMaxOriginal)) * 0.05 : dataRange * 0.05;

  const initialYMin = yMinOriginal - padding;
  const initialYMax = yMaxOriginal + padding;

  return {
    yChartMin: initialYMin === initialYMax ? initialYMin - 0.05 : initialYMin,
    yChartMax: initialYMin === initialYMax ? initialYMax + 0.05 : initialYMax,
  };
}
